/**
 * Motor Vital Pflegeberater (Produktfinder v5)
 *
 * Four answers → one reasoned system. State lives in the URL
 * (?antrieb=…&fahrzeug=…&profil=…&ziele=…) so the flow survives reloads,
 * back navigation and campaign deep links. Sets are applied automatically
 * whenever they cover the selected components.
 */
( function () {
	'use strict';

	var root = document.querySelector( '[data-mv-pf]' );
	if ( ! root ) {
		return;
	}

	/* ── Payload ─────────────────────────────────────────────────────────── */

	var payload = {};
	try {
		payload = JSON.parse( root.parentNode.querySelector( '[data-mv-pf-payload]' ).textContent );
	} catch ( error ) {
		return;
	}
	var products = payload.products || {};
	delete products._;
	var roles = payload.roles || {};
	var sets = ( payload.sets || [] ).filter( function ( s ) { return s && s.handle && products[ s.handle ]; } );
	var threshold = Number( payload.threshold || root.getAttribute( 'data-threshold' ) || 0 );
	var collections = payload.collections || {};

	/* ── Vocabulary ──────────────────────────────────────────────────────── */

	var STEPS = [ 'drive', 'vehicle', 'profile', 'goals' ];
	var PARAM = { drive: 'antrieb', vehicle: 'fahrzeug', profile: 'profil', goals: 'ziele' };
	var LABELS = {
		drive: { benzin: 'Benzin', diesel: 'Diesel', 'hybrid-benzin': 'Hybrid (Benzin)', 'hybrid-diesel': 'Hybrid (Diesel)' },
		vehicle: { pkw: 'Pkw', transporter: 'Transporter / Wohnmobil', lkw: 'Lkw / Nutzfahrzeug', landmaschine: 'Land- / Baumaschine' },
		profile: { kurz: 'viel Kurzstrecke', gemischt: 'gemischtes Fahrprofil', lang: 'viel Langstrecke' },
		goals: { vorsorge: 'Vorsorge', verbrauch: 'Verbrauch senken', leistung: 'Leistung', abgas: 'Abgas-Warnung', oelwechsel: 'Ölwechsel', laufleistung: 'über 100.000 km' }
	};
	var ROLE_LABELS = {
		kraftstoff: 'Kraftstoffsystem',
		dpf: 'Partikelfilter',
		nanotec: 'Motoröl & Verschleiß',
		reiniger: 'Ölkreislauf',
		systemreiniger: 'Problemlöser Diesel',
		kat: 'Katalysator & OPF'
	};

	/* ── State ───────────────────────────────────────────────────────────── */

	var state = { drive: '', vehicle: '', profile: '', goals: [], goalsDone: false };

	function readState() {
		var q = new URLSearchParams( location.search );
		state.drive = LABELS.drive[ q.get( PARAM.drive ) ] ? q.get( PARAM.drive ) : '';
		state.vehicle = LABELS.vehicle[ q.get( PARAM.vehicle ) ] ? q.get( PARAM.vehicle ) : '';
		state.profile = LABELS.profile[ q.get( PARAM.profile ) ] ? q.get( PARAM.profile ) : '';
		if ( q.has( PARAM.goals ) ) {
			state.goals = q.get( PARAM.goals ).split( ',' ).filter( function ( g ) { return LABELS.goals[ g ]; } );
			state.goalsDone = true;
		}
	}

	function writeState() {
		var q = new URLSearchParams( location.search );
		Object.keys( PARAM ).forEach( function ( key ) { q.delete( PARAM[ key ] ); } );
		if ( state.drive ) { q.set( PARAM.drive, state.drive ); }
		if ( state.vehicle ) { q.set( PARAM.vehicle, state.vehicle ); }
		if ( state.profile ) { q.set( PARAM.profile, state.profile ); }
		if ( state.goalsDone ) { q.set( PARAM.goals, state.goals.join( ',' ) ); }
		var qs = q.toString();
		history.replaceState( null, '', location.pathname + ( qs ? '?' + qs : '' ) + location.hash );
	}

	function answered( step ) {
		return step === 'goals' ? state.goalsDone : Boolean( state[ step ] );
	}

	function firstOpenStep() {
		for ( var i = 0; i < STEPS.length; i += 1 ) {
			if ( ! answered( STEPS[ i ] ) ) {
				return STEPS[ i ];
			}
		}
		return 'result';
	}

	/* ── Helpers ─────────────────────────────────────────────────────────── */

	var prefersReducedMotion = window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
	var moneyFormat = new Intl.NumberFormat( 'de-DE', { style: 'currency', currency: 'EUR' } );

	function money( minor ) {
		return moneyFormat.format( ( Number( minor ) || 0 ) / 100 );
	}

	function esc( value ) {
		return String( value == null ? '' : value ).replace( /[&<>"']/g, function ( c ) {
			return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ c ];
		} );
	}

	function track( name, data ) {
		try {
			if ( window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function' ) {
				window.Shopify.analytics.publish( name, data || {} );
			}
		} catch ( error ) {}
	}

	function fuelOf() {
		return state.drive.indexOf( 'diesel' ) !== -1 ? 'diesel' : 'benzin';
	}

	function sizeOf() {
		return state.vehicle === 'pkw' ? '250' : '1000';
	}

	function has( goal ) {
		return state.goals.indexOf( goal ) !== -1;
	}

	function roleProduct( role ) {
		var byFuel = roles[ role ] || {};
		var bySize = byFuel[ fuelOf() ] || byFuel.any;
		if ( ! bySize ) {
			return null;
		}
		var handle = bySize[ sizeOf() ] || bySize[ '250' ];
		return handle && products[ handle ] ? products[ handle ] : null;
	}

	/* ── Recommendation engine ───────────────────────────────────────────── */

	function buildRecommendation() {
		var fuel = fuelOf();
		var size = sizeOf();
		var kurz = state.profile === 'kurz';
		var items = [];
		var extras = [];

		function push( list, role, product, reason, note, usage ) {
			if ( ! product ) {
				return;
			}
			list.push( { role: role, roleLabel: ROLE_LABELS[ role ], product: product, reason: reason, note: note || '', usage: usage } );
		}

		/* Kraftstoffsystem — always the base. */
		var kraftstoff = roleProduct( 'kraftstoff' );
		var kReason;
		if ( fuel === 'diesel' ) {
			kReason = kurz
				? 'Häufige Kaltstarts belasten die Injektoren – hält das Einspritzsystem sauber und stabilisiert die Verbrennung.'
				: has( 'verbrauch' )
					? 'Saubere Injektoren bedeuten vollständige Verbrennung – der direkteste Hebel auf den Verbrauch.'
					: 'Reinigt Einspritzsystem und Injektoren kontinuierlich und schützt vor Korrosion – die Basis jeder Dieselpflege.';
		} else {
			kReason = kurz
				? 'Kurzstrecken hinterlassen Ablagerungen in Einspritzung und Brennraum – reinigt bei jeder Fahrt mit.'
				: has( 'verbrauch' ) || has( 'leistung' )
					? 'Reinigt das Kraftstoffsystem und senkt die Reibung am oberen Kolbenring – spürbar bei Leistung und Verbrauch.'
					: 'Reinigt Einspritzung und Kraftstoffsystem, schützt vor Korrosion – die Basis jeder Benzinpflege.';
		}
		push( items, 'kraftstoff', kraftstoff, kReason, size === '1000' ? 'Großgebinde – passend zu großen Tanks und hoher Laufleistung.' : '', 'Bei jedem Tanken dem Kraftstoff zugeben – eine Dose reicht für bis zu ' + ( size === '1000' ? '1.000' : '250' ) + ' Liter.' );

		/* Partikelfilter — Diesel only. */
		if ( fuel === 'diesel' ) {
			var dReason = kurz
				? 'Kurzstrecken sind die häufigste Ursache für verstopfte Partikelfilter: Der Ruß erreicht nie seine Abbrandtemperatur. DPF-Schutz senkt sie auf rund 350 °C, damit die Regeneration auch im Stadtverkehr läuft.'
				: has( 'abgas' )
					? 'Bei Partikelfilter-Warnung: unterstützt den Rußabbrand katalytisch und ist die vorbeugende Alternative zum teuren Filtertausch.'
					: 'Hält den Partikelfilter dauerhaft regenerationsfähig – vorbeugen ist deutlich günstiger als tauschen.';
			push( items, 'dpf', roleProduct( 'dpf' ), dReason, 'Nur bei vorhandenem Dieselpartikelfilter (Serie seit ca. 2009).', 'Zusammen mit dem Diesel-Additiv beim Tanken zugeben.' );
		}

		/* Motoröl — always. */
		var nReason = has( 'laufleistung' )
			? 'Härtet die Reibflächen im Motor chemisch – gerade bei hoher Laufleistung der wirksamste Verschleißschutz.'
			: has( 'verbrauch' )
				? 'Weniger Reibung heißt weniger Verbrauch und ein ruhigerer Motorlauf.'
				: has( 'leistung' )
					? 'Stabilisiert Kompression und Laufkultur und senkt Geräusche.'
					: 'Molekulare Innenbeschichtung überall, wo Reibung, Druck und Hitze herrschen – reduziert Verschleiß nachhaltig.';
		push( items, 'nanotec', roleProduct( 'nanotec' ), nReason, '', 'Ins Motoröl geben – ideal direkt nach dem Ölwechsel ins frische Öl.' );

		/* Ölkreislauf — in the system when an oil change or high mileage is on the table. */
		var reiniger = roleProduct( 'reiniger' );
		var rUsage = 'Vor dem Ölwechsel ins alte Öl geben, kurz laufen lassen, dann Öl wechseln.';
		if ( has( 'oelwechsel' ) ) {
			push( items, 'reiniger', reiniger, 'Vor dem Ölwechsel: löst Ablagerungen im gesamten Ölkreislauf, damit das frische Öl sauber arbeitet.', '', rUsage );
		} else if ( has( 'laufleistung' ) ) {
			push( items, 'reiniger', reiniger, 'Ab 100.000 km sammeln sich Ablagerungen im Ölkreislauf – die Reinigung sichert die Ölversorgung.', '', rUsage );
		} else {
			push( extras, 'reiniger', reiniger, 'Sinnvoll, wenn der nächste Ölwechsel ansteht: reinigt den gesamten Ölkreislauf.', '', rUsage );
		}

		/* Problem solvers. */
		if ( fuel === 'diesel' ) {
			var sr = roleProduct( 'systemreiniger' );
			if ( has( 'abgas' ) || has( 'leistung' ) ) {
				push( items, 'systemreiniger', sr, has( 'abgas' ) ? 'Akute Kur bei Abgas-Warnung oder Leistungsverlust: reinigt das gesamte Dieselsystem intensiv.' : 'Einmalige Intensivreinigung des Dieselsystems – bringt Leistung und Laufkultur zurück.', 'Einmalige Anwendung, danach genügt die kontinuierliche Pflege.', 'Einmalig als Kur in den Tank – dann den Tank leerfahren.' );
			} else if ( has( 'laufleistung' ) ) {
				push( extras, 'systemreiniger', sr, 'Optionale Intensivkur für Diesel mit hoher Laufleistung.', '', 'Einmalig als Kur in den Tank.' );
			}
		} else {
			var kat = roleProduct( 'kat' );
			if ( has( 'abgas' ) ) {
				push( items, 'kat', kat, 'Bei Kat- oder OPF-Warnung: reinigt Katalysator und Ottopartikelfilter während der Fahrt – ohne Werkstatt.', 'Einmalige Anwendung.', 'Einmalig als Kur in den Tank – dann den Tank leerfahren.' );
			} else if ( kurz || has( 'leistung' ) ) {
				push( extras, 'kat', kat, kurz ? 'Bei viel Kurzstrecke versotten Kat und OPF schneller – reinigt während der Fahrt.' : 'Bringt Leistung zurück, wenn Kat oder OPF belastet sind.', '', 'Einmalig als Kur in den Tank.' );
			}
		}

		return { fuel: fuel, size: size, items: items, extras: extras };
	}

	/* ── Set matching ────────────────────────────────────────────────────── */

	function bestSet( checkedHandles ) {
		var best = null;
		sets.forEach( function ( s ) {
			var covered = s.components.every( function ( h ) { return checkedHandles.indexOf( h ) !== -1; } );
			if ( ! covered || ! products[ s.handle ] || ! products[ s.handle ].inStock ) {
				return;
			}
			var single = s.components.reduce( function ( sum, h ) { return sum + Number( products[ h ] ? products[ h ].priceMinor : 0 ); }, 0 );
			var saving = single - Number( products[ s.handle ].priceMinor );
			if ( saving <= 0 ) {
				return;
			}
			if ( ! best || s.components.length > best.components.length || ( s.components.length === best.components.length && saving > best.saving ) ) {
				best = { handle: s.handle, components: s.components, saving: saving, product: products[ s.handle ] };
			}
		} );
		return best;
	}

	function buildOrder( checkedHandles ) {
		var set = bestSet( checkedHandles );
		var lines = [];
		var singleTotal = 0;
		checkedHandles.forEach( function ( h ) { singleTotal += Number( products[ h ].priceMinor ); } );
		if ( set ) {
			lines.push( { handle: set.handle, product: set.product, isSet: true, components: set.components } );
		}
		checkedHandles.forEach( function ( h ) {
			if ( set && set.components.indexOf( h ) !== -1 ) {
				return;
			}
			lines.push( { handle: h, product: products[ h ], isSet: false } );
		} );
		var total = lines.reduce( function ( sum, l ) { return sum + Number( l.product.priceMinor ); }, 0 );
		return { lines: lines, total: total, saving: singleTotal - total, set: set };
	}

	/* ── DOM refs ────────────────────────────────────────────────────────── */

	var stage = root.querySelector( '[data-mv-pf-stage]' );
	var panels = {};
	root.querySelectorAll( '[data-mv-pf-step]' ).forEach( function ( el ) { panels[ el.getAttribute( 'data-mv-pf-step' ) ] = el; } );
	var resultEl = root.querySelector( '[data-mv-pf-result]' );
	var progressItems = {};
	root.querySelectorAll( '[data-mv-pf-progress-step]' ).forEach( function ( el ) { progressItems[ el.getAttribute( 'data-mv-pf-progress-step' ) ] = el; } );
	var componentList = root.querySelector( '[data-mv-pf-component-list]' );
	var extraList = root.querySelector( '[data-mv-pf-extra-list]' );
	var extrasWrap = root.querySelector( '[data-mv-pf-extras]' );
	var planList = root.querySelector( '[data-mv-pf-plan-list]' );
	var summaryLines = root.querySelector( '[data-mv-pf-summary-lines]' );
	var summarySet = root.querySelector( '[data-mv-pf-summary-set]' );
	var summaryTotal = root.querySelector( '[data-mv-pf-summary-total]' );
	var summarySaving = root.querySelector( '[data-mv-pf-summary-saving]' );
	var summaryShipping = root.querySelector( '[data-mv-pf-summary-shipping]' );
	var addButton = root.querySelector( '[data-mv-pf-add]' );
	var statusEl = root.querySelector( '[data-mv-pf-status]' );
	var answersEl = root.querySelector( '[data-mv-pf-answers]' );
	var titleEl = root.querySelector( '[data-mv-pf-result-title]' );
	var kickerEl = root.querySelector( '[data-mv-pf-result-kicker]' );
	var countEl = root.querySelector( '[data-mv-pf-component-count]' );
	var goalsNote = root.querySelector( '[data-mv-pf-goals-note]' );
	var mobileBar = root.querySelector( '[data-mv-pf-mobile-bar]' );
	var mobileLines = root.querySelector( '[data-mv-pf-mobile-lines]' );
	var mobileTotal = root.querySelector( '[data-mv-pf-mobile-total]' );
	var mobileAdd = root.querySelector( '[data-mv-pf-mobile-add]' );

	var current = null;
	var recommendation = null;
	var checked = [];

	/* ── Navigation ──────────────────────────────────────────────────────── */

	function labelFor( step ) {
		if ( step === 'goals' ) {
			return state.goals.length ? state.goals.map( function ( g ) { return LABELS.goals[ g ]; } ).join( ', ' ) : 'Grundpflege';
		}
		return LABELS[ step ][ state[ step ] ] || '';
	}

	function updateProgress() {
		Object.keys( progressItems ).forEach( function ( step ) {
			var el = progressItems[ step ];
			var done = step === 'result' ? current === 'result' : answered( step );
			el.classList.toggle( 'is-done', done && step !== 'result' );
			el.classList.toggle( 'is-active', current === step );
			el.classList.toggle( 'is-clickable', step !== 'result' && ( done || step === current ) );
			if ( current === step ) {
				el.setAttribute( 'aria-current', 'step' );
			} else {
				el.removeAttribute( 'aria-current' );
			}
			var valueEl = el.querySelector( '[data-mv-pf-progress-value]' );
			if ( valueEl ) {
				valueEl.textContent = done && step !== 'result' ? labelFor( step ) : '';
			}
		} );
	}

	function syncInputs() {
		STEPS.slice( 0, 3 ).forEach( function ( step ) {
			panels[ step ].querySelectorAll( '[data-mv-pf-answer="' + step + '"]' ).forEach( function ( input ) {
				input.checked = input.value === state[ step ];
				input.closest( '.mv-pf__option' ).classList.toggle( 'is-selected', input.checked );
			} );
		} );
		panels.goals.querySelectorAll( '[data-mv-pf-goal]' ).forEach( function ( input ) {
			input.checked = has( input.value );
			input.closest( '.mv-pf__chip' ).classList.toggle( 'is-selected', input.checked );
		} );
		if ( goalsNote ) {
			goalsNote.textContent = state.goals.length ? state.goals.length + ' Ziel' + ( state.goals.length > 1 ? 'e' : '' ) + ' gewählt' : 'Grundpflege ohne Zusatzziel';
		}
	}

	function show( step, focus ) {
		current = step;
		Object.keys( panels ).forEach( function ( key ) { panels[ key ].hidden = key !== step; } );
		resultEl.hidden = step !== 'result';
		root.classList.toggle( 'is-result', step === 'result' );
		document.body.classList.toggle( 'mv-pf-result', step === 'result' );
		if ( mobileBar ) {
			mobileBar.hidden = step !== 'result';
		}
		updateProgress();
		syncInputs();
		if ( step === 'result' ) {
			renderResult();
		}
		if ( focus ) {
			var target = step === 'result' ? titleEl : panels[ step ].querySelector( '.mv-pf__question' );
			if ( target ) {
				target.focus( { preventScroll: true } );
			}
			var rect = stage.getBoundingClientRect();
			if ( rect.top < 80 || rect.top > window.innerHeight * 0.5 ) {
				var top = window.scrollY + rect.top - 96;
				window.scrollTo( { top: Math.max( 0, top ), behavior: prefersReducedMotion ? 'auto' : 'smooth' } );
			}
		}
	}

	function advance() {
		var next = firstOpenStep();
		show( next, true );
	}

	/* ── Answer handling ─────────────────────────────────────────────────── */

	root.querySelectorAll( '[data-mv-pf-answer]' ).forEach( function ( input ) {
		input.addEventListener( 'change', function () {
			var step = input.getAttribute( 'data-mv-pf-answer' );
			state[ step ] = input.value;
			/* Pack size and fuel change the product set: a changed answer re-opens the result fresh. */
			checked = [];
			writeState();
			syncInputs();
			track( 'mv_finder_step', { step: step, value: input.value } );
			window.setTimeout( advance, prefersReducedMotion ? 0 : 220 );
		} );
	} );

	root.querySelectorAll( '[data-mv-pf-goal]' ).forEach( function ( input ) {
		input.addEventListener( 'change', function () {
			state.goals = Array.prototype.map.call( panels.goals.querySelectorAll( '[data-mv-pf-goal]:checked' ), function ( i ) { return i.value; } );
			syncInputs();
		} );
	} );

	root.querySelector( '[data-mv-pf-finish]' ).addEventListener( 'click', function () {
		state.goalsDone = true;
		checked = [];
		writeState();
		track( 'mv_finder_step', { step: 'goals', value: state.goals.join( ',' ) || 'none' } );
		show( 'result', true );
	} );

	var evToggle = root.querySelector( '[data-mv-pf-ev-toggle]' );
	var evNote = root.querySelector( '[data-mv-pf-ev-note]' );
	if ( evToggle && evNote ) {
		evToggle.addEventListener( 'click', function () {
			var open = evNote.hidden;
			evNote.hidden = ! open;
			evToggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		} );
	}

	Object.keys( progressItems ).forEach( function ( step ) {
		if ( step === 'result' ) {
			return;
		}
		progressItems[ step ].addEventListener( 'click', function () {
			if ( progressItems[ step ].classList.contains( 'is-clickable' ) ) {
				show( step, true );
			}
		} );
	} );

	root.querySelector( '[data-mv-pf-restart]' ).addEventListener( 'click', function () {
		state = { drive: '', vehicle: '', profile: '', goals: [], goalsDone: false };
		checked = [];
		writeState();
		track( 'mv_finder_step', { step: 'restart', value: '' } );
		show( 'drive', true );
	} );

	/* ── Result rendering ────────────────────────────────────────────────── */

	var setCovered = [];

	function componentRow( entry, isChecked, listName ) {
		var p = entry.product;
		var id = 'mv-pf-c-' + p.id.replace( /[^a-z0-9]/gi, '' );
		return '<li class="mv-pf__component' + ( isChecked ? ' is-checked' : '' ) + ( p.inStock ? '' : ' is-unavailable' ) + '">' +
			'<label class="mv-pf__component-inner" for="' + id + '">' +
				'<input type="checkbox" id="' + id + '" data-mv-pf-comp="' + esc( p.id ) + '" data-mv-pf-list="' + listName + '"' + ( isChecked ? ' checked' : '' ) + ( p.inStock ? '' : ' disabled' ) + '>' +
				'<span class="mv-pf__component-check" aria-hidden="true"></span>' +
				( p.imageUrl ? '<img class="mv-pf__component-img" src="' + esc( p.imageUrl ) + '" alt="" width="72" height="72" loading="lazy" decoding="async">' : '<span class="mv-pf__component-img"></span>' ) +
				'<span class="mv-pf__component-body">' +
					'<span class="mv-pf__component-role">' + esc( entry.roleLabel ) + '</span>' +
					'<strong class="mv-pf__component-title">' + esc( p.title.replace( /^MOTOR VITAL /, '' ) ) + '</strong>' +
					'<span class="mv-pf__component-set" data-mv-pf-set-badge' + ( setCovered.indexOf( p.id ) !== -1 ? '' : ' hidden' ) + '>Im Set – günstiger, automatisch angewendet</span>' +
					'<span class="mv-pf__component-reason">' + esc( entry.reason ) + '</span>' +
					( entry.note ? '<span class="mv-pf__component-note">' + esc( entry.note ) + '</span>' : '' ) +
					'<a class="mv-pf__component-link" href="' + esc( p.permalink ) + '">Details</a>' +
				'</span>' +
				'<span class="mv-pf__component-price">' + ( p.inStock ? money( p.priceMinor ) : 'Nicht verfügbar' ) + '</span>' +
			'</label>' +
		'</li>';
	}

	function renderResult() {
		recommendation = buildRecommendation();
		var all = recommendation.items.concat( recommendation.extras );
		if ( ! checked.length ) {
			checked = recommendation.items.filter( function ( e ) { return e.product.inStock; } ).map( function ( e ) { return e.product.id; } );
		} else {
			/* keep only handles that still exist in the new recommendation */
			var valid = all.map( function ( e ) { return e.product.id; } );
			checked = checked.filter( function ( h ) { return valid.indexOf( h ) !== -1; } );
		}

		var fuelLabel = recommendation.fuel === 'diesel' ? 'Diesel' : 'Benzin';
		titleEl.textContent = 'Ihr Pflegesystem für ' + fuelLabel + ' · ' + LABELS.vehicle[ state.vehicle ] + ' · ' + LABELS.profile[ state.profile ];
		kickerEl.textContent = recommendation.items.length + ' Produkte' + ( recommendation.size === '1000' ? ' · Großgebinde' : '' );

		answersEl.innerHTML = STEPS.map( function ( step ) {
			return '<li><button type="button" class="mv-pf__answer" data-mv-pf-edit="' + step + '"><span>' + esc( { drive: 'Antrieb', vehicle: 'Fahrzeug', profile: 'Fahrprofil', goals: 'Ziele' }[ step ] ) + '</span><strong>' + esc( labelFor( step ) ) + '</strong><em>ändern</em></button></li>';
		} ).join( '' );
		answersEl.querySelectorAll( '[data-mv-pf-edit]' ).forEach( function ( btn ) {
			btn.addEventListener( 'click', function () { show( btn.getAttribute( 'data-mv-pf-edit' ), true ); } );
		} );

		componentList.innerHTML = recommendation.items.map( function ( e ) { return componentRow( e, checked.indexOf( e.product.id ) !== -1, 'system' ); } ).join( '' );
		extraList.innerHTML = recommendation.extras.map( function ( e ) { return componentRow( e, checked.indexOf( e.product.id ) !== -1, 'extra' ); } ).join( '' );
		extrasWrap.hidden = recommendation.extras.length === 0;

		root.querySelectorAll( '[data-mv-pf-comp]' ).forEach( function ( input ) {
			input.addEventListener( 'change', function () {
				var h = input.getAttribute( 'data-mv-pf-comp' );
				if ( input.checked && checked.indexOf( h ) === -1 ) {
					checked.push( h );
				} else if ( ! input.checked ) {
					checked = checked.filter( function ( x ) { return x !== h; } );
				}
				input.closest( '.mv-pf__component' ).classList.toggle( 'is-checked', input.checked );
				renderSummary();
				renderPlan();
			} );
		} );

		renderSummary();
		renderPlan();

		track( 'mv_finder_results', {
			fuel: recommendation.fuel,
			vehicle: state.vehicle,
			profile: state.profile,
			goals: state.goals.join( ',' ),
			product_count: recommendation.items.length,
			size: recommendation.size
		} );
	}

	function renderPlan() {
		var order = [ 'reiniger', 'nanotec', 'kraftstoff', 'dpf', 'systemreiniger', 'kat' ];
		var all = recommendation.items.concat( recommendation.extras ).filter( function ( e ) { return checked.indexOf( e.product.id ) !== -1; } );
		all.sort( function ( a, b ) { return order.indexOf( a.role ) - order.indexOf( b.role ); } );
		planList.innerHTML = all.map( function ( e ) {
			return '<li><strong>' + esc( e.product.title.replace( /^MOTOR VITAL /, '' ) ) + '</strong><span>' + esc( e.usage ) + '</span></li>';
		} ).join( '' );
		planList.parentNode.hidden = all.length === 0;
	}

	function shortTitle( product ) {
		return product.title.replace( /^MOTOR VITAL /, '' );
	}

	function renderSummary() {
		var order = buildOrder( checked );
		var productCount = checked.length;
		setCovered = order.set ? order.set.components : [];
		root.querySelectorAll( '[data-mv-pf-comp]' ).forEach( function ( input ) {
			var badge = input.closest( '.mv-pf__component' ).querySelector( '[data-mv-pf-set-badge]' );
			if ( badge ) {
				badge.hidden = setCovered.indexOf( input.getAttribute( 'data-mv-pf-comp' ) ) === -1;
			}
		} );
		countEl.textContent = productCount ? '· ' + productCount + ' ausgewählt' : '';

		var kicker = root.querySelector( '[data-mv-pf-summary-kicker]' );
		if ( kicker ) {
			kicker.textContent = productCount ? productCount + ( productCount === 1 ? ' Produkt' : ' Produkte' ) + ' für Ihr System' : 'Ihre Auswahl';
		}

		summaryLines.innerHTML = order.lines.map( function ( l ) {
			if ( l.isSet ) {
				var singleSum = l.components.reduce( function ( sum, h ) { return sum + Number( products[ h ].priceMinor ); }, 0 );
				return '<li class="is-set">' +
					'<div class="mv-pf__summary-set-head"><span><em>Set</em> ' + l.components.length + ' Produkte · 1 Artikel</span><strong>' + money( l.product.priceMinor ) + '</strong></div>' +
					'<ul class="mv-pf__summary-set-items">' + l.components.map( function ( h ) {
						return '<li><span>✓</span>' + esc( shortTitle( products[ h ] ) ) + '</li>';
					} ).join( '' ) + '</ul>' +
					'<div class="mv-pf__summary-set-saving">Einzeln ' + money( singleSum ) + ' · als Set ' + money( l.product.priceMinor ) + ' → <strong>' + money( singleSum - l.product.priceMinor ) + ' gespart</strong></div>' +
				'</li>';
			}
			return '<li><span>' + esc( shortTitle( l.product ) ) + '</span><strong>' + money( l.product.priceMinor ) + '</strong></li>';
		} ).join( '' ) || '<li class="is-empty"><span>Keine Position ausgewählt</span></li>';

		summaryTotal.textContent = money( order.total );
		if ( order.saving > 0 ) {
			summarySaving.hidden = false;
			summarySaving.textContent = 'Set-Vorteil bereits abgezogen: Sie sparen ' + money( order.saving ) + ' gegenüber dem Einzelkauf.';
		} else {
			summarySaving.hidden = true;
		}

		if ( threshold > 0 && order.total > 0 ) {
			summaryShipping.textContent = order.total >= threshold ? 'Kostenloser Versand inklusive · Lieferung in 1–2 Werktagen' : 'Noch ' + money( threshold - order.total ) + ' bis zum kostenlosen Versand';
			summaryShipping.classList.toggle( 'is-unlocked', order.total >= threshold );
		} else {
			summaryShipping.textContent = '';
		}

		/* Button counts products (what the customer receives), the note explains the cart lines. */
		addButton.disabled = order.lines.length === 0;
		addButton.textContent = productCount === 0
			? 'Bitte mindestens ein Produkt wählen'
			: ( productCount === 1 ? '1 Produkt' : productCount + ' Produkte' ) + ' in den Warenkorb · ' + money( order.total );

		if ( order.set ) {
			var rest = order.lines.length - 1;
			summarySet.hidden = false;
			summarySet.innerHTML = '<strong>So landet es im Warenkorb:</strong> ' + order.set.components.length + ' Ihrer ' + productCount + ' Produkte gibt es günstiger als Set – das Set erscheint dort als <strong>ein Artikel</strong>' + ( rest > 0 ? ', dazu ' + rest + ( rest === 1 ? ' einzelnes Produkt' : ' einzelne Produkte' ) : '' ) + '. Sie erhalten alle ' + productCount + ' Produkte, der Set-Vorteil von ' + money( order.set.saving ) + ' ist schon eingerechnet.';
		} else {
			summarySet.hidden = true;
		}

		if ( mobileBar ) {
			mobileLines.textContent = productCount === 0 ? 'Keine Auswahl' : productCount + ( productCount === 1 ? ' Produkt' : ' Produkte' ) + ( order.saving > 0 ? ' · Set-Vorteil ' + money( order.saving ) : '' );
			mobileTotal.textContent = money( order.total );
			mobileAdd.disabled = order.lines.length === 0;
		}
		addButton.dataset.mvLines = JSON.stringify( order.lines.map( function ( l ) { return l.product.purchaseId; } ) );
		addButton.dataset.mvValue = String( order.total );
		addButton.dataset.mvSet = order.set ? order.set.handle : '';
	}

	/* ── Add to cart ─────────────────────────────────────────────────────── */

	addButton.addEventListener( 'click', async function () {
		var ids;
		try { ids = JSON.parse( addButton.dataset.mvLines || '[]' ); } catch ( error ) { ids = []; }
		if ( ! ids.length || addButton.disabled ) {
			return;
		}
		var label = addButton.textContent;
		addButton.disabled = true;
		addButton.classList.add( 'is-loading' );
		addButton.textContent = 'Wird hinzugefügt …';
		statusEl.textContent = '';
		track( 'mv_finder_system_add', {
			package_id: addButton.dataset.mvSet || 'custom',
			items: checked,
			item_count: ids.length,
			value: Number( addButton.dataset.mvValue || 0 ) / 100,
			currency: 'EUR',
			fuel: recommendation ? recommendation.fuel : '',
			vehicle: state.vehicle
		} );
		try {
			for ( var i = 0; i < ids.length; i += 1 ) {
				if ( window.MotorVitalCart && typeof window.MotorVitalCart.add === 'function' ) {
					await window.MotorVitalCart.add( ids[ i ], 1, { open: false, feedback: 'none' } );
				} else {
					var response = await fetch( '/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify( { items: [ { id: ids[ i ], quantity: 1 } ] } ) } );
					if ( ! response.ok ) { throw new Error( 'add failed' ); }
				}
			}
			if ( window.MotorVitalCart && typeof window.MotorVitalCart.open === 'function' ) {
				window.MotorVitalCart.open();
			} else {
				window.location.href = '/cart';
			}
			statusEl.textContent = 'Ihr Pflegesystem liegt im Warenkorb – ' + ids.length + ( ids.length === 1 ? ' Artikel' : ' Artikel' ) + ', alle ' + checked.length + ' Produkte enthalten.';
			addButton.classList.remove( 'is-loading' );
			addButton.classList.add( 'is-added' );
			addButton.textContent = 'Im Warenkorb ✓';
			window.setTimeout( function () {
				addButton.classList.remove( 'is-added' );
				addButton.textContent = label;
				addButton.disabled = false;
			}, 2400 );
		} catch ( error ) {
			addButton.classList.remove( 'is-loading' );
			addButton.textContent = label;
			addButton.disabled = false;
			statusEl.textContent = 'Das hat nicht geklappt – bitte versuchen Sie es erneut.';
		}
	} );

	if ( mobileAdd ) {
		mobileAdd.addEventListener( 'click', function () {
			addButton.click();
			mobileAdd.textContent = 'Wird hinzugefügt …';
			window.setTimeout( function () { mobileAdd.textContent = 'In den Warenkorb'; }, 2400 );
		} );
	}

	/* ── Keyboard: arrow keys move through option cards, Enter confirms ── */

	root.querySelectorAll( '.mv-pf__options' ).forEach( function ( group ) {
		group.addEventListener( 'keydown', function ( event ) {
			if ( event.key !== 'Enter' ) {
				return;
			}
			var input = event.target.closest( '.mv-pf__option' ) && event.target.closest( '.mv-pf__option' ).querySelector( 'input' );
			if ( input && ! input.checked ) {
				input.checked = true;
				input.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			}
		} );
	} );

	/* ── Boot ────────────────────────────────────────────────────────────── */

	readState();
	root.classList.add( 'is-ready' );
	show( firstOpenStep(), false );

	window.addEventListener( 'popstate', function () {
		readState();
		checked = [];
		show( firstOpenStep(), false );
	} );
} )();
